"use client";
import PlayButton from "@/components/home/lavender-carpet/play-button";
import { Image } from "@/components/shared/image";
import { useTranslation } from "@/i18n/client";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

interface VideoProps {
  src: string;
  thumbnail: string;
  lang: Lang;
}

function Video({ src, thumbnail, lang }: VideoProps) {
  const { t } = useTranslation(lang, "common");

  const videoRef = useRef<HTMLVideoElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const inview = useInView(viewRef as React.RefObject<Element>, {
    amount: 0.6,
  });

  useEffect(() => {
    if (inview && !isPaused) {
      const video = videoRef.current;
      if (!video) return;

      video.play();
      setIsPlaying(true);
      setIsHovering(false);
    } else {
      const video = videoRef.current;
      if (!video) return;

      video.pause();
      setIsPlaying(false);
      setIsHovering(true);
    }
  }, [inview, isPaused]);

  function play() {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
      setIsPaused(false);
    } else {
      video.pause();
      setIsPlaying(false);
      //
      setIsPaused(true);
    }
  }

  return (
    <div ref={viewRef} className="lavender-carpet relative flex h-full w-full rounded-[40px]">
      {isPaused && (
        <div className="absolute inset-0 w-full">
          <Image
            src={thumbnail}
            width={5000}
            height={5000}
            alt="Image"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="z-0 h-full w-full"
          onClick={() => {
            if (isPaused) return;
            setIsHovering(!isHovering);
          }}
          style={{
            background:
              "linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), \
           linear-gradient(180deg, rgba(0, 0, 0, 0) 91.28%, #000000 100%), \
           linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 10.47%)",
            visibility: !isHovering ? "hidden" : "visible",
          }}
        />

        <div className="container absolute bottom-[16%] z-[100] landscape:sm:bottom-0 landscape:md:bottom-[16%]">
          <div className={cn("flex items-center gap-4", !isHovering ? "hidden" : "flex")}>
            <PlayButton
              isPlaying={isPlaying}
              className={cn("h-16 w-16 xl:h-20 xl:w-20")}
              onClick={play}
            />
            {isPlaying ? <p>{t("pauseVideo")}</p> : <p>{t("playVideo")}</p>}
          </div>
        </div>
      </div>
      <video
        ref={videoRef}
        onClick={() => {
          if (!isHovering) {
            setIsHovering(true);
          }
        }}
        playsInline
        muted
        autoPlay
        loop
        poster={thumbnail}
      >
        <source src={src} />
      </video>

      <div className="pointer-events-none absolute inset-x-0 -top-4 h-1/3 bg-gradient-to-b from-white dark:from-background"></div>
      <div className="pointer-events-none absolute inset-x-0 -bottom-4 h-1/3 bg-gradient-to-t from-white dark:from-background"></div>
    </div>
  );
}

export default Video;
